import React, { useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { apiError, fmtD, fmtDT } from "@/lib/platform";
import { downloadEvidence } from "@/lib/rights";
import { Panel } from "@/components/rights/RightsBadges";
import { EvidenceUploadButton } from "@/components/rights/EvidenceUploadButton";

const Deliver = ({ r, resp, meta, onDone }) => {
  const [channel, setChannel] = useState("");
  const [on, setOn] = useState("");
  const submit = async () => {
    try {
      await api.post(`/rights/${r.id}/responses/${resp.id}/deliver`, { channel, delivered_on: on || null });
      toast.success("Delivery recorded");
      onDone();
    } catch (e) { toast.error(apiError(e, "Could not record delivery")); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`deliver-form-${resp.id}`}>
      <Select value={channel} onValueChange={setChannel}>
        <SelectTrigger className="h-8 w-[170px] text-[11px]" data-testid={`deliver-channel-${resp.id}`}><SelectValue placeholder="Delivered via" /></SelectTrigger>
        <SelectContent>{Object.entries(meta.delivery_channels).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="date" value={on} onChange={(e) => setOn(e.target.value)} className="h-8 w-[150px] text-[11px]" data-testid={`deliver-date-${resp.id}`} />
      <Button size="sm" disabled={!channel} onClick={submit} className="h-8 bg-[#003366] hover:bg-[#003366]/90" data-testid={`deliver-submit-${resp.id}`}>
        <Send className="w-3 h-3 mr-1" /> Mark delivered
      </Button>
    </div>
  );
};

export const ResponsePanel = ({ r, meta, canEdit, onChanged }) => {
  const [tpl, setTpl] = useState(r.request_type);
  const [busy, setBusy] = useState(false);
  const editable = canEdit && r.status !== "CLOSED";
  const allowed = [r.request_type, "VERIFICATION_FAILED"];
  const template = meta.templates.find((t) => t.key === tpl);
  const canGenerate = tpl === "VERIFICATION_FAILED" ? r.verification_status === "FAILED" : ["REVIEW", "RESPOND"].includes(r.status);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rights/${r.id}/responses`, { template_key: tpl });
      toast.success(`Response letter v${data.version} generated from the fixed template`);
      onChanged();
    } catch (e) { toast.error(apiError(e, "Could not generate letter")); } finally { setBusy(false); }
  };

  return (
    <Panel eyebrow="Fixed templates only · no AI-generated text" title="Response letter" testId="response-panel"
           actions={editable && <>
             <Select value={tpl} onValueChange={setTpl}>
               <SelectTrigger className="h-8 w-[220px] text-[11px]" data-testid="response-template-select"><SelectValue /></SelectTrigger>
               <SelectContent>{meta.templates.filter((t) => allowed.includes(t.key)).map((t) => <SelectItem key={t.key} value={t.key}>{t.title}</SelectItem>)}</SelectContent>
             </Select>
             <Button size="sm" onClick={generate} disabled={busy || !canGenerate} className="bg-[#008080] hover:bg-[#008080]/90" data-testid="response-generate-button">
               <FileText className="w-3.5 h-3.5 mr-1" /> Generate letter
             </Button>
           </>}>
      {r.responses.length === 0 ? (
        <div data-testid="response-template-preview">
          <div className="text-[11px] text-slate-500 mb-2">
            {canGenerate ? "Template preview. Merge fields are filled from this request when you generate the letter." :
              "Letters are generated in the Review or Respond stage. Template preview:"}
          </div>
          <pre className="text-[11px] leading-relaxed whitespace-pre-wrap font-mono bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-[320px] overflow-y-auto text-slate-600">
            {template?.body}
          </pre>
        </div>
      ) : (
        <div className="space-y-4">
          {r.responses.map((x) => (
            <div key={x.id} className="border border-slate-200 rounded-lg" data-testid={`response-${x.id}`}>
              <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-100 flex-wrap">
                <div className="text-xs">
                  <span className="font-semibold text-[#003366]">v{x.version} · {x.title}</span>
                  <span className="text-slate-500"> · {x.generated_by} · {fmtDT(x.generated_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  {x.delivered_at ? (
                    <span className="text-[11px] font-semibold text-emerald-700" data-testid={`response-delivered-${x.id}`}>
                      Delivered {fmtD(x.delivered_at)} · {meta.delivery_channels[x.delivery_channel]}
                    </span>
                  ) : <span className="text-[11px] text-amber-700" data-testid={`response-undelivered-${x.id}`}>Not delivered yet</span>}
                  <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => downloadEvidence(x.evidence_file_id)}
                          data-testid={`response-download-${x.id}`}><Download className="w-3 h-3 mr-1" /> .txt</Button>
                </div>
              </div>
              <pre className="text-[11px] leading-relaxed whitespace-pre-wrap font-mono p-4 max-h-[260px] overflow-y-auto text-slate-700">{x.body}</pre>
              {editable && (
                <div className="px-4 pb-3 flex items-center gap-3 flex-wrap">
                  {!x.delivered_at && <Deliver r={r} resp={x} meta={meta} onDone={onChanged} />}
                  <EvidenceUploadButton requestId={r.id} category="DELIVERY_RECEIPT" onUploaded={onChanged} label="Delivery receipt"
                                        testId={`response-receipt-${x.id}`} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};
